/**
 * Sidebar navigation, declared once and filtered by RBAC at render time.
 * Add a route + a NavItem here to surface a new section; access gating is
 * declarative via `permission` / `role` / `minRole`.
 */
import {
  BankOutlined,
  FolderOpenOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { ROUTE_PATHS } from './app.config'
import type { CheckMode, RbacSubject } from '@/lib/rbac'
import type { ReactNode } from 'react'
import type { RoutePath } from './app.config'
import { Permission, UserRole, canAccess, hasMinRole, hasRole } from '@/lib/rbac'

export interface NavItem {
  key: string
  label: string
  /** Per-role label overrides — e.g. Compliance Officer sees a different name. */
  labelByRole?: Partial<Record<UserRole, string>>
  /** In-app route path. Omit for pure grouping items. */
  path?: string
  icon?: ReactNode
  /** Required permission(s) to see this item. */
  permission?: Permission | Array<Permission>
  /** Allowed role(s). */
  role?: UserRole | Array<UserRole>
  /** Minimum role authority. */
  minRole?: UserRole
  /**
   * Role(s) that must NOT see this item, applied last and overriding every
   * check above. This is the escape hatch for "everyone but X" rules, which
   * permissions cannot express: the super admin holds `ALL_PERMISSIONS`, so any
   * new permission is granted to them by construction (see `permissions.ts`).
   * Reach for `permission` first — this is only for excluding a role outright.
   */
  hideForRole?: UserRole | Array<UserRole>
  children?: Array<NavItem>
}

export const NAV_ITEMS: Array<NavItem> = [
  {
    // All roles submit/view checks.
    key: 'verifications',
    label: 'Verifications',
    path: ROUTE_PATHS.verifications,
    icon: <SafetyCertificateOutlined />,
  },
  {
    // Cases (AMLIQ-253) — new area built alongside Verifications, WIP/mock.
    // AMLIQ-334 AC#6 hides it from the super admin: cases belong to a tenant
    // and the platform role has no tenant scope. The menu only — the route
    // itself stays reachable by URL, which is what the AC asks for.
    key: 'cases',
    label: 'Cases',
    path: ROUTE_PATHS.cases,
    icon: <FolderOpenOutlined />,
    hideForRole: UserRole.SystemAdmin,
  },
  // Temporarily hidden — Learn Centre ships in a later phase.
  // {
  //   // Learn Centre is readable by all roles (managing it is gated elsewhere).
  //   // Compliance Officers see it branded as "Compliance".
  //   key: 'learn-centre',
  //   label: 'Learn Centre',
  //   labelByRole: { [UserRole.ComplianceOfficer]: 'Compliance' },
  //   path: ROUTE_PATHS.learnCentre,
  //   icon: <BookOutlined />,
  // },
  {
    // Cross-account subscriber list — AMLiQ super-admin only.
    key: 'accounts',
    label: 'Accounts',
    path: ROUTE_PATHS.accounts,
    icon: <BankOutlined />,
    permission: Permission.AccountManage,
  },
  {
    // Invite/manage users — account admins, compliance officers & super-admin (not standard users).
    key: 'user-management',
    label: 'User Management',
    path: ROUTE_PATHS.userManagement,
    icon: <TeamOutlined />,
    permission: Permission.UserManage,
  },
  {
    // Account profile, subscription & settings — account admins & super-admin.
    key: 'account-settings',
    label: 'Account Settings',
    path: ROUTE_PATHS.accountSettings,
    icon: <SettingOutlined />,
    permission: Permission.AccountSettingsManage,
  },
  {
    // Support is available to all roles.
    key: 'support',
    label: 'Support',
    path: ROUTE_PATHS.support,
    icon: <QuestionCircleOutlined />,
  },
]

/** Subset of permission checks needed to evaluate nav visibility. */
export interface NavChecks {
  canAccess: (permissions: ReadonlyArray<Permission>, mode?: CheckMode) => boolean
  hasRole: (roles: UserRole | ReadonlyArray<UserRole>) => boolean
  hasMinRole: (min: UserRole) => boolean
}

/** The label to show for an item, applying any per-role override. */
export function resolveNavLabel(item: NavItem, checks: NavChecks): string {
  if (item.labelByRole) {
    for (const role of Object.keys(item.labelByRole) as Array<UserRole>) {
      if (checks.hasRole(role)) return item.labelByRole[role] as string
    }
  }
  return item.label
}

export function isNavItemVisible(item: NavItem, checks: NavChecks): boolean {
  // Exclusions win over everything, so a role can be denied an item it would
  // otherwise reach through its permissions.
  if (item.hideForRole && checks.hasRole(item.hideForRole)) return false
  if (item.permission) {
    const perms = Array.isArray(item.permission) ? item.permission : [item.permission]
    if (!checks.canAccess(perms)) return false
  }
  if (item.role && !checks.hasRole(item.role)) return false
  if (item.minRole && !checks.hasMinRole(item.minRole)) return false
  return true
}

/** First visible, navigable path in the tree (depth-first), or `undefined`. */
function firstVisiblePath(items: Array<NavItem>, checks: NavChecks): string | undefined {
  for (const item of items) {
    if (!isNavItemVisible(item, checks)) continue
    if (item.path) return item.path
    if (item.children) {
      const childPath = firstVisiblePath(item.children, checks)
      if (childPath) return childPath
    }
  }
  return undefined
}

/**
 * The post-login landing path: the first sidebar tab the subject's role and
 * permissions allow. There is no fixed home page — where a user lands depends
 * on what they can see. Falls back to `/unauthorized` if nothing is visible.
 */
export function resolveLandingPath(subject: RbacSubject): RoutePath {
  const checks: NavChecks = {
    canAccess: (perms, mode) => canAccess(subject, perms, mode),
    hasRole: (roles) => hasRole(subject, roles),
    hasMinRole: (min) => hasMinRole(subject, min),
  }
  return (firstVisiblePath(NAV_ITEMS, checks) ?? ROUTE_PATHS.unauthorized) as RoutePath
}
